import { CanvasObjectModel } from "@/hooks/use-remote-requests.";
import { AnnotationMode, DrawingModel, MinimalSessionDtoInterface, RoomModel, StudentModel, UnitAndLessonModel } from "@/models";
import { PositionPayloadInterface } from "@/store/room/interface";
import { MediaDeviceTypes } from "@/utils/utils";
import type { TokenDetails, TokenRequest } from "ably";
import { IndependentStudentItem } from "../independent";

export interface BaseResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  code?: number;
}
export type RealtimeTokenResponse = BaseResponse<TokenRequest | TokenDetails>;
export interface NextSessionResponse {
  classInfo: {
    classId: string;
    className: string;
    groupId: string;
    groupName: string;
    lesson: number;
    startTime: null | string;
    unit: number;
  };
  nextTime: null | string;
  sessionId: string | null;
  studentId: string;
}

export interface SessionStateModel {
  startMediaAt: null | string;
  duration: number;
  pauseMediaAt: null | string;
}

export type FetchSessionDtoResModel = BaseResponse<RoomModel>;
export type UnitAndLessonResponse = BaseResponse<UnitAndLessonModel[]>;
export type GetMinimalSessionDtoResponse = BaseResponse<MinimalSessionDtoInterface>;
export type SessionReplacementResponse = BaseResponse<{}>;
export type SessionStateResponse = BaseResponse<SessionStateModel>;
export type CheckSessionStartResp = BaseResponse<{}>;
export type ToggleStudentPalettesResp = BaseResponse<{}>;
export type SetStudentsBadgeResp = BaseResponse<{}>;
export type ClearStudentRaisingHandResp = BaseResponse<{}>;
export type FetchOngoingSessionModel = {
  sessionId: string;
  groupId: string;
  classId: string;
  deviceId: string;
  isHelper: boolean;
};

export enum MainRoles {
  TEACHER = "teacher",
  HELPER = "helper",
  STUDENT = "student",
}
export interface PutRemoteUserDisconnectedReqModel {
  localUserId: string;
  remoteUserRole: MainRoles;
  remoteUserId: string;
  sessionId: string;
}
export interface TeacherUpdateMediaStateRequestModel {
  isOneToOne: boolean;
  sessionId: string;
}

export interface TeacherSetOneOneRequestModel {
  sessionId: string;
  studentId?: string | null;
}

export interface TeacherUpdateAlternateState {
  isPlay: boolean;
  duration: number;
}

export interface PostLocalUserReconnectedReqModel {
  localUserRole: MainRoles;
  localUserId: string;
  sessionId: string;
}

export interface FetchTeamsReqModel {
  sessionId: string;
}
export interface TeamDto {
  id: string | null;
  name: string;
  color: string;
  order: number;
  studentIds: string[];
}
export interface OneToOneDto {
  studentId: string;
  selectedExposureId: string;
  selectedExposureItemId: string;
  isEnablePalette?: boolean;
  isShowWhiteBoard: boolean;
  drawing: null | DrawingModel;
  student: null | StudentModel;
  teachingMode: number;
  lessonAction: number;
  ratio: number;
  position: PositionPayloadInterface;
  studentIndependents: null | IndependentStudentItem[];
  isIgnoreTeacherAudio: null | boolean;
  ratioScrollPdf: number;
  startMediaAt: null | string;
  pauseMediaAt: null | string;
  duration: number;
}

export interface TeamRevisionDto {
  teams: TeamDto[];
  revision: string;
}

export type FetchTeamsResModel = BaseResponse<TeamRevisionDto>;

export interface PutToggleTeamModeReqModel {
  isOpen: boolean;
  sessionId: string;
}
export type PutToggleTeamModeResModel = BaseResponse<boolean>;

export interface PutTeamsReqModel {
  sessionId: string;
  teams: TeamDto[];
  revision: number;
}

export interface BaseRequestModel {
  sessionId: string;
  isOneOne: boolean;
  isTeacherUseOnly: boolean;
}

export interface TeacherUpdateCanvasObjectRequestModel extends BaseRequestModel {
  canvasObject: CanvasObjectModel;
}

export interface TeacherUpdateAnnotationModeRequestModel extends BaseRequestModel {
  mode: AnnotationMode;
}

export interface ToggleStudentMediaDevicesAsync {
  mediaDeviceType: MediaDeviceTypes;
  studentIds: string[];
  isMute: boolean;
}

export interface ToggleStudentPaletteAsync {
  studentId: string;
  isEnable: boolean;
  isHelper: boolean;
  isOneOne: boolean;
}
export interface ToggleStudentPaletteReq extends ToggleStudentPaletteAsync {
  sessionId: string;
}

export interface SetStudentsBadgeReq {
  sessionId: string;
  studentIds: string[];
}

export type PutTeamsResModel = BaseResponse<any>;

export type FetchVideoOnTeacherDisconnect = {
  url: string;
};

export interface TeacherJoinSessionModel {
  sessionId: string;
  deviceId: string;
  resolution?: string;
  teacherId: string;
}
export type HelperJoinSessionModel = TeacherJoinSessionModel;
